import React, { useCallback, useEffect, useState } from 'react';
import AppHeader from './AppHeader';
import {
  createCompany,
  createSubscription,
  getCompanies,
  updateCompany,
  updateSubscription,
} from '../api';
import { SUBSCRIPTION_PLANS, SUBSCRIPTION_STATUSES } from '../utils/roles';

const EMPTY_COMPANY = {
  name: '',
  contactName: '',
  contactEmail: '',
  plan: 'trial',
  seats: 5,
};

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

const emptyNewSubscription = () => ({
  plan: 'professional',
  status: 'active',
  seats: 5,
  startsAt: todayIsoDate(),
  endsAt: '',
  notes: '',
});

function Corporate({ user, onLogout, onNavigate }) {
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_COMPANY);
  const [editingCompany, setEditingCompany] = useState(null);
  const [newSubscription, setNewSubscription] = useState(emptyNewSubscription);
  const [savingSubscriptionId, setSavingSubscriptionId] = useState(null);
  const [subForm, setSubForm] = useState({
    companyId: '',
    plan: 'professional',
    status: 'active',
    seats: 10,
    notes: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCompanies();
      const list = data.companies || [];
      setCompanies(list);
      setSubForm((current) =>
        current.companyId || !list.length
          ? current
          : { ...current, companyId: list[0].id }
      );
    } catch (err) {
      setError(err.error || 'Unable to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await createCompany({
        name: form.name.trim(),
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(),
        plan: form.plan,
        seats: Number(form.seats) || 5,
        createSubscription: true,
      });
      setForm(EMPTY_COMPANY);
      setMessage('Company and subscription created');
      await load();
    } catch (err) {
      setError(err.error || 'Unable to create company');
    }
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await createSubscription({
        companyId: subForm.companyId,
        plan: subForm.plan,
        status: subForm.status,
        seats: Number(subForm.seats) || 1,
        notes: subForm.notes.trim(),
      });
      setMessage('Subscription created');
      await load();
    } catch (err) {
      setError(err.error || 'Unable to create subscription');
    }
  };

  const toggleCompanyActive = async (company) => {
    setError('');
    try {
      await updateCompany(company.id, { ...company, active: !company.active });
      await load();
    } catch (err) {
      setError(err.error || 'Unable to update company');
    }
  };

  const syncEditingCompany = (list, companyId) => {
    const match = (list || []).find((entry) => entry.id === companyId);
    if (!match) {
      setEditingCompany(null);
      return;
    }
    setEditingCompany({
      id: match.id,
      name: match.name || '',
      slug: match.slug || '',
      contactName: match.contactName || '',
      contactEmail: match.contactEmail || '',
      active: Boolean(match.active),
      subscriptions: (match.subscriptions || []).map((sub) => ({
        ...sub,
        startsAt: (sub.startsAt || '').slice(0, 10),
        endsAt: (sub.endsAt || '').slice(0, 10),
      })),
    });
  };

  const openEditCompany = (company) => {
    syncEditingCompany([company], company.id);
    setNewSubscription(emptyNewSubscription());
    setError('');
    setMessage('');
  };

  const handleEditCompanyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingCompany((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubscriptionFieldChange = (subscriptionId, field, value) => {
    setEditingCompany((prev) => ({
      ...prev,
      subscriptions: (prev.subscriptions || []).map((sub) =>
        sub.id === subscriptionId ? { ...sub, [field]: value } : sub
      ),
    }));
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    if (!editingCompany) return;
    setError('');
    setMessage('');
    try {
      await updateCompany(editingCompany.id, {
        name: editingCompany.name.trim(),
        slug: editingCompany.slug.trim(),
        contactName: editingCompany.contactName.trim(),
        contactEmail: editingCompany.contactEmail.trim(),
        active: editingCompany.active,
      });
      setMessage(`Updated ${editingCompany.name || editingCompany.id}`);
      setEditingCompany(null);
      await load();
    } catch (err) {
      setError(err.error || 'Unable to update company');
    }
  };

  const handleSaveSubscriptionDates = async (subscription) => {
    setError('');
    setMessage('');
    setSavingSubscriptionId(subscription.id);
    try {
      const payload = {
        plan: subscription.plan,
        status: subscription.status,
        seats: Number(subscription.seats) || 1,
        startsAt: subscription.startsAt || todayIsoDate(),
        endsAt: subscription.endsAt || '',
        notes: subscription.notes || '',
      };
      if (payload.endsAt && payload.status !== 'canceled') {
        payload.status = 'canceled';
      }
      await updateSubscription(subscription.id, payload);
      setMessage(`Subscription ${subscription.id} updated`);
      const data = await getCompanies();
      const list = data.companies || [];
      setCompanies(list);
      syncEditingCompany(list, editingCompany.id);
    } catch (err) {
      setError(err.error || 'Unable to update subscription');
    } finally {
      setSavingSubscriptionId(null);
    }
  };

  const handleAddSubscriptionToCompany = async (e) => {
    e.preventDefault();
    if (!editingCompany) return;
    if (!newSubscription.startsAt) {
      setError('Start date is required for a new subscription');
      return;
    }
    setError('');
    setMessage('');
    try {
      await createSubscription({
        companyId: editingCompany.id,
        plan: newSubscription.plan,
        status: newSubscription.status,
        seats: Number(newSubscription.seats) || 1,
        startsAt: newSubscription.startsAt,
        endsAt: newSubscription.endsAt || '',
        notes: newSubscription.notes.trim(),
      });
      setMessage('Subscription added');
      setNewSubscription(emptyNewSubscription());
      const data = await getCompanies();
      const list = data.companies || [];
      setCompanies(list);
      syncEditingCompany(list, editingCompany.id);
    } catch (err) {
      setError(err.error || 'Unable to add subscription');
    }
  };

  const setSubscriptionStatus = async (subscription, status) => {
    setError('');
    try {
      await updateSubscription(subscription.id, { status });
      await load();
    } catch (err) {
      setError(err.error || 'Unable to update subscription');
    }
  };

  return (
    <div className="container wide-container">
      <AppHeader
        user={user}
        pageTitle="Corporate"
        subtitle="Create companies and manage subscriptions"
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="corporate"
      />

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <section className="card-section" style={{ marginTop: 20 }}>
        <h2>New company</h2>
        <p className="subtitle">Creates the company and an initial subscription in one step.</p>
        <form onSubmit={handleCreateCompany}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="co-name">Company name *</label>
              <input
                id="co-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="co-plan">Initial plan</label>
              <select
                id="co-plan"
                value={form.plan}
                onChange={(e) => setForm({ ...form, plan: e.target.value })}
              >
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <option key={plan} value={plan}>
                    {plan}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="co-contact">Contact name</label>
              <input
                id="co-contact"
                value={form.contactName}
                onChange={(e) => setForm({ ...form, contactName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="co-email">Contact email</label>
              <input
                id="co-email"
                type="email"
                value={form.contactEmail}
                onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="co-seats">Seats</label>
              <input
                id="co-seats"
                type="number"
                min="1"
                value={form.seats}
                onChange={(e) => setForm({ ...form, seats: e.target.value })}
              />
            </div>
          </div>
          <button type="submit">Create company</button>
        </form>
      </section>

      <section className="card-section" style={{ marginTop: 28 }}>
        <h2>Add subscription</h2>
        <form onSubmit={handleCreateSubscription}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sub-company">Company</label>
              <select
                id="sub-company"
                value={subForm.companyId}
                onChange={(e) => setSubForm({ ...subForm, companyId: e.target.value })}
                required
              >
                <option value="">Select</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="sub-plan">Plan</label>
              <select
                id="sub-plan"
                value={subForm.plan}
                onChange={(e) => setSubForm({ ...subForm, plan: e.target.value })}
              >
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <option key={plan} value={plan}>
                    {plan}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="sub-status">Status</label>
              <select
                id="sub-status"
                value={subForm.status}
                onChange={(e) => setSubForm({ ...subForm, status: e.target.value })}
              >
                {SUBSCRIPTION_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="sub-seats">Seats</label>
              <input
                id="sub-seats"
                type="number"
                min="1"
                value={subForm.seats}
                onChange={(e) => setSubForm({ ...subForm, seats: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="sub-notes">Notes</label>
            <input
              id="sub-notes"
              value={subForm.notes}
              onChange={(e) => setSubForm({ ...subForm, notes: e.target.value })}
            />
          </div>
          <button type="submit">Add subscription</button>
        </form>
      </section>

      <section className="card-section" style={{ marginTop: 28 }}>
        <h2>Companies</h2>
        {loading ? (
          <p className="subtitle">Loading…</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Subscriptions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td>
                      <strong>{company.name}</strong>
                      <div className="subtitle" style={{ margin: 0 }}>
                        {company.id} · {company.slug}
                      </div>
                    </td>
                    <td>
                      {company.contactName || '—'}
                      <div className="subtitle" style={{ margin: 0 }}>
                        {company.contactEmail || ''}
                      </div>
                    </td>
                    <td>{company.active ? 'Active' : 'Inactive'}</td>
                    <td>
                      {(company.subscriptions || []).length === 0 ? (
                        <span className="subtitle">None</span>
                      ) : (
                        <ul className="entitlements-list compact">
                          {company.subscriptions.map((sub) => (
                            <li key={sub.id}>
                              {sub.plan} · {sub.status} · {sub.seats} seats{' '}
                              {sub.status !== 'canceled' && (
                                <button
                                  type="button"
                                  className="linkish"
                                  onClick={() => setSubscriptionStatus(sub, 'canceled')}
                                >
                                  Cancel
                                </button>
                              )}
                              {sub.status === 'canceled' && (
                                <button
                                  type="button"
                                  className="linkish"
                                  onClick={() => setSubscriptionStatus(sub, 'active')}
                                >
                                  Reactivate
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="table-actions-cell">
                      <div className="table-actions-inner">
                        <button
                          type="button"
                          className="btn-table"
                          onClick={() => openEditCompany(company)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-table btn-secondary-table"
                          onClick={() => toggleCompanyActive(company)}
                        >
                          {company.active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editingCompany && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-wide">
            <div className="modal-header">
              <h2>Edit company {editingCompany.id}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setEditingCompany(null)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSaveCompany}>
              <div className="form-group">
                <label htmlFor="edit-co-name">Company name *</label>
                <input
                  id="edit-co-name"
                  name="name"
                  value={editingCompany.name}
                  onChange={handleEditCompanyChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-co-slug">Slug</label>
                <input
                  id="edit-co-slug"
                  name="slug"
                  value={editingCompany.slug}
                  onChange={handleEditCompanyChange}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-co-contact">Contact name</label>
                  <input
                    id="edit-co-contact"
                    name="contactName"
                    value={editingCompany.contactName}
                    onChange={handleEditCompanyChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-co-email">Contact email</label>
                  <input
                    id="edit-co-email"
                    name="contactEmail"
                    type="email"
                    value={editingCompany.contactEmail}
                    onChange={handleEditCompanyChange}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="active"
                    checked={Boolean(editingCompany.active)}
                    onChange={handleEditCompanyChange}
                  />{' '}
                  Active account
                </label>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setEditingCompany(null)}
                >
                  Close
                </button>
                <button type="submit">Save profile</button>
              </div>
            </form>

            <div className="company-subs-panel">
              <h3>Subscriptions</h3>
              <p className="subtitle">
                Set an end date to close a subscription, or add a new one with a start date.
              </p>
              <div className="table-scroll">
                <table className="compensation-table company-subs-table">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Seats</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(editingCompany.subscriptions || []).length === 0 ? (
                      <tr>
                        <td colSpan={6} className="subtitle" style={{ textAlign: 'center' }}>
                          No subscriptions yet.
                        </td>
                      </tr>
                    ) : (
                      (editingCompany.subscriptions || []).map((sub) => (
                        <tr key={sub.id}>
                          <td>
                            <select
                              aria-label={`Plan for ${sub.id}`}
                              value={sub.plan}
                              onChange={(e) =>
                                handleSubscriptionFieldChange(sub.id, 'plan', e.target.value)
                              }
                            >
                              {SUBSCRIPTION_PLANS.map((plan) => (
                                <option key={plan} value={plan}>
                                  {plan}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              aria-label={`Status for ${sub.id}`}
                              value={sub.status}
                              onChange={(e) =>
                                handleSubscriptionFieldChange(sub.id, 'status', e.target.value)
                              }
                            >
                              {SUBSCRIPTION_STATUSES.map((status) => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              aria-label={`Seats for ${sub.id}`}
                              value={sub.seats}
                              onChange={(e) =>
                                handleSubscriptionFieldChange(sub.id, 'seats', e.target.value)
                              }
                              style={{ width: 72 }}
                            />
                          </td>
                          <td>
                            <input
                              type="date"
                              aria-label={`Start date for ${sub.id}`}
                              value={sub.startsAt || ''}
                              onChange={(e) =>
                                handleSubscriptionFieldChange(sub.id, 'startsAt', e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="date"
                              aria-label={`End date for ${sub.id}`}
                              value={sub.endsAt || ''}
                              onChange={(e) =>
                                handleSubscriptionFieldChange(sub.id, 'endsAt', e.target.value)
                              }
                            />
                          </td>
                          <td className="table-actions-cell">
                            <div className="table-actions-inner">
                              <button
                                type="button"
                                className="btn-table"
                                disabled={savingSubscriptionId === sub.id}
                                onClick={() => handleSaveSubscriptionDates(sub)}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn-table btn-secondary-table"
                                disabled={savingSubscriptionId === sub.id}
                                onClick={() =>
                                  handleSaveSubscriptionDates({
                                    ...sub,
                                    endsAt: todayIsoDate(),
                                    status: 'canceled',
                                  })
                                }
                              >
                                End today
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <h4 style={{ marginTop: 20 }}>Add subscription</h4>
              <form onSubmit={handleAddSubscriptionToCompany} className="company-add-sub-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="new-sub-plan">Plan</label>
                    <select
                      id="new-sub-plan"
                      value={newSubscription.plan}
                      onChange={(e) =>
                        setNewSubscription({ ...newSubscription, plan: e.target.value })
                      }
                    >
                      {SUBSCRIPTION_PLANS.map((plan) => (
                        <option key={plan} value={plan}>
                          {plan}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="new-sub-status">Status</label>
                    <select
                      id="new-sub-status"
                      value={newSubscription.status}
                      onChange={(e) =>
                        setNewSubscription({ ...newSubscription, status: e.target.value })
                      }
                    >
                      {SUBSCRIPTION_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="new-sub-seats">Seats</label>
                    <input
                      id="new-sub-seats"
                      type="number"
                      min="1"
                      value={newSubscription.seats}
                      onChange={(e) =>
                        setNewSubscription({ ...newSubscription, seats: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="new-sub-start">Start date *</label>
                    <input
                      id="new-sub-start"
                      type="date"
                      required
                      value={newSubscription.startsAt}
                      onChange={(e) =>
                        setNewSubscription({ ...newSubscription, startsAt: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="new-sub-end">End date</label>
                    <input
                      id="new-sub-end"
                      type="date"
                      value={newSubscription.endsAt}
                      onChange={(e) =>
                        setNewSubscription({ ...newSubscription, endsAt: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="new-sub-notes">Notes</label>
                    <input
                      id="new-sub-notes"
                      value={newSubscription.notes}
                      onChange={(e) =>
                        setNewSubscription({ ...newSubscription, notes: e.target.value })
                      }
                    />
                  </div>
                </div>
                <button type="submit">Add subscription</button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Corporate;
